import React from 'react';
import { Link } from 'react-router-dom';

/**
 * The answer to a plain-language policy question, and its citations
 * (GitHub issue #173).
 *
 * MODEL OUTPUT IS A PLAIN REACT TEXT NODE IN A pre-wrap BLOCK -- never
 * RichContentView, never RedlineView, never dangerouslySetInnerHTML. That is
 * DraftingAssistantPanel's rule and it applies with more force here: the
 * `quote` blocks are verbatim spans of somebody's policy, and the answer is
 * generated prose. Neither has been through content_sanitizer, and neither
 * should be rendered as markup.
 *
 * EVERY QUOTE SHOWN HERE HAS BEEN VERIFIED against the text of the policy it
 * is attributed to -- answer_schema.verify_citations drops anything it cannot
 * find, and an answer with nothing left never reaches this component at all
 * (the page renders the word-matched results instead). So a reader can take a
 * quote here and find it in the linked policy, which is the whole basis on
 * which this feature asks to be trusted.
 */

function FallbackNotice({ reason, quota }) {
  if (!reason) return null;

  // Deliberately NOT QuotaLimitModal, which every other AI panel uses. Nothing
  // was refused to the reader here -- they got results, just word-matched ones
  // -- so a modal demanding a click would be an interruption announcing a
  // downgrade they may not even care about.
  const messages = {
    unavailable: 'Answering questions is not switched on here, so these are word matches.',
    quota: 'You have reached your AI usage limit, so these are word matches for now.',
    failed: 'The answer service could not be reached, so these are word matches.',
    stub: 'AI is not configured in this environment, so these are word matches.',
    unanswered: 'No policy here answers that question directly. These mention what you asked about.',
  };
  const message = messages[reason] || 'These are word matches.';

  let detail = null;
  if (reason === 'quota' && quota) {
    const hours = Math.max(1, Math.round((quota.retry_after_seconds || 0) / 3600));
    detail = ` You have used ${quota.used} of ${quota.limit} allowed ${
      quota.window_hours}-hour request(s); try again in about ${hours} hour(s).`;
  }

  return (
    <div className="alert alert-info" style={{ marginBottom: '15px' }}>
      🔤 {message}{detail}
    </div>
  );
}

function Citation({ citation }) {
  return (
    <li style={{ marginBottom: '14px' }}>
      <div>
        {/* state.from: DocumentViewPage's back button returns here via browser
            history rather than to the generic Documents list -- ApprovalsPage/
            InsightsPage's own precedent, applied to the Start Page. */}
        <Link to={`/documents/${citation.document_id}`} state={{ from: '/' }}>
          <strong>{citation.title}</strong>
        </Link>
        {citation.document_type && (
          <span className="badge" style={{
            marginLeft: '8px', background: 'var(--neutral-bg)', color: 'var(--neutral-ink)',
          }}>
            {citation.document_type}
          </span>
        )}
        {citation.location && (
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>
            {citation.location}
          </div>
        )}
      </div>
      {/* The policy's own words, verified verbatim. pre-wrap and a plain text
          node -- see this file's header. */}
      <blockquote style={{
        margin: '6px 0 0', padding: '8px 12px',
        borderLeft: '3px solid var(--teal-500)', background: 'var(--surface-alt)',
        whiteSpace: 'pre-wrap', fontSize: '13px',
      }}>
        {citation.quote}
      </blockquote>
      {citation.note && (
        <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
          {citation.note}
        </div>
      )}
    </li>
  );
}

function Related({ related, answered }) {
  if (!related || related.length === 0) return null;
  return (
    <div className="card" style={{ marginTop: '15px' }}>
      <div className="card-header">
        <h3>Related documents</h3>
      </div>
      {/* Conditional, because on every fallback path there IS no answer above
          -- and a line referring to one that is not on screen reads as a bug. */}
      <p style={{ fontSize: '12px', color: 'var(--muted)', margin: '0 0 10px' }}>
        {answered
          ? 'Not quoted in the answer above, but they mention what you asked about.'
          : 'These mention what you asked about.'}
      </p>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {related.map(item => (
          <li key={item.document_id} style={{ marginBottom: '10px' }}>
            <Link to={`/documents/${item.document_id}`} state={{ from: '/' }}>
              {item.title}
            </Link>
            {item.document_type && (
              <span className="badge" style={{
                marginLeft: '8px', background: 'var(--neutral-bg)', color: 'var(--neutral-ink)',
              }}>
                {item.document_type}
              </span>
            )}
            <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>
              {item.location}
              {/* The shortlist pass's own sentence when it ran, the
                  deterministic reasons otherwise -- so this line says something
                  honest either way. */}
              {(item.semantic_reason || item.reasons?.length > 0) && (
                <> · {item.semantic_reason || item.reasons.join('; ')}</>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SearchAnswer({ result }) {
  if (!result) return null;

  return (
    <>
      <FallbackNotice reason={result.fallback_reason} quota={result.quota} />

      {result.is_answered && (
        <div className="card" style={{ marginBottom: '15px' }}>
          <div className="card-header">
            <h3>🤖 Answer</h3>
          </div>
          <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
            {result.answer}
          </div>

          <h4 style={{ margin: '18px 0 8px', fontSize: '14px', color: 'var(--ink)' }}>
            From these documents
          </h4>
          <ol style={{ paddingLeft: '18px', margin: 0 }}>
            {result.citations.map((citation, index) => (
              <Citation key={`${citation.document_id}-${index}`}
                        citation={citation} />
            ))}
          </ol>

          {result.unverified_citations > 0 && (
            <p style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '12px' }}>
              {result.unverified_citations} further citation(s) were left out because the
              quoted wording could not be found in the policy.
            </p>
          )}

          <p style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '12px' }}>
            Answered from your organization's active documents only. Check the quoted
            text against the document before acting on it.
          </p>
        </div>
      )}

      <Related related={result.related} answered={result.is_answered} />
    </>
  );
}

export default SearchAnswer;
